import { Badge } from '@artic/ui/badge';
import { Button } from '@artic/ui/button';
import { DatePicker } from '@artic/ui/date-picker';
import { StandardDate } from '@artic/ui/date-tooltip';
import { useFeatureFlag } from '@artic/ui/feature-flags';
import { formatStandardDate } from '@artic/ui/format-standard-date';
import { Input } from '@artic/ui/input';
import { Label } from '@artic/ui/label';
import { LabeledDate } from '@artic/ui/labeled-date';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@artic/ui/sheet';
import { Skeleton } from '@artic/ui/skeleton';
import { UserInfo } from '@artic/ui/user-info';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryState } from 'nuqs';
import { useState } from 'react';

import { formatIsoDate, parseIsoDate } from '../../components/iso-date';
import { usePartnerId } from '../../features/inbox/use-partner-name';
import { EditProjectDialog } from '../../features/project-planning/edit-project-dialog';
import { ExcelImportDialog } from '../../features/project-planning/excel-import/excel-import-dialog';
import { ProjectListRowActions } from '../../features/project-planning/project-list-row-actions';
import { resolveProjectMilestone } from '../../features/project-planning/project-milestone-date';
import { ProjectsListViewToggle } from '../../features/project-planning/projects-list-view-toggle';
import {
  projectPlanningListQueryOptions,
  useCreateProject,
} from '../../features/project-planning/queries';
import { filterProjects, projectSearchParsers } from '../../features/project-planning/search';
import type { ProjectPlanningListItem, ProjectStatus } from '../../features/project-planning/types';

export const Route = createFileRoute('/projects/')({
  component: ProjectListView,
});

// ─── Feature flag gate ────────────────────────────────────────────

function ProjectListView() {
  const planner = useFeatureFlag('planner');

  if (!planner) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Feature Not Available</h2>
        <p className="text-muted-foreground text-sm">
          Production Planning is not enabled for your organisation.
        </p>
      </div>
    );
  }

  return <ProjectListContent />;
}

// ─── Status badge variant mapping ─────────────────────────────────

// ENG-830: `project_status` is a stored tri-state (`draft | released |
// redrafting`) — the list endpoint returns it directly, so the row's badge
// reads `project.project_status` with no derivation.
const statusVariant: Record<ProjectStatus, 'default' | 'secondary'> = {
  draft: 'default',
  released: 'secondary',
  redrafting: 'default',
};

// ─── Creation sheet ───────────────────────────────────────────────

function CreateProjectSheet() {
  const partnerId = usePartnerId();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [prefix, setPrefix] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const createProject = useCreateProject();

  function resetForm() {
    setName('');
    setCode('');
    setPrefix('');
    setStartDate('');
    setEndDate('');
    setFormError(null);
  }

  function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setFormError(null);

    if (!partnerId) {
      setFormError('Partner not resolved. Please try again.');
      return;
    }

    if (prefix && !/^[A-Z0-9]{2,8}$/.test(prefix)) {
      setFormError('Project prefix must be 2–8 uppercase alphanumeric characters.');
      return;
    }

    if (startDate && endDate && endDate < startDate) {
      setFormError('End date cannot be before start date.');
      return;
    }

    createProject
      .mutateAsync({
        partner_id: partnerId,
        project_name: name,
        project_code: code,
        project_prefix: prefix || null,
        project_scheduled_start_date: startDate || null,
        project_scheduled_complete_date: endDate || null,
      })
      .then((project) => {
        resetForm();
        setOpen(false);
        void navigate({
          to: '/projects/$projectId',
          params: { projectId: project.project_id },
        });
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'Failed to create project.';
        setFormError(message);
      });
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button size="sm">New Project</Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Create Project</SheetTitle>
          <SheetDescription>
            Create a new production project. You can add work orders after creation.
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-name">Project Name</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder="e.g. Riverside Block A"
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-code">Project Code</Label>
            <Input
              id="project-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
              placeholder="e.g. RIV-A"
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-prefix">Work Order Prefix (optional)</Label>
            <Input
              id="project-prefix"
              value={prefix}
              onChange={(e) => {
                setPrefix(e.target.value.toUpperCase());
              }}
              placeholder="2–8 uppercase alphanumeric"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-start">Start Date</Label>
            <DatePicker
              id="project-start"
              aria-label="Start Date"
              value={parseIsoDate(startDate)}
              onValueChange={(date) => {
                setStartDate(formatIsoDate(date));
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-end">End Date</Label>
            <DatePicker
              id="project-end"
              aria-label="End Date"
              value={parseIsoDate(endDate)}
              onValueChange={(date) => {
                setEndDate(formatIsoDate(date));
              }}
            />
          </div>
          {formError && <p className="text-destructive text-sm">{formError}</p>}
          <Button type="submit" disabled={createProject.isPending} className="mt-2">
            {createProject.isPending ? 'Creating...' : 'Create Project'}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

// ─── Partner-level import ─────────────────────────────────────────

/**
 * Import work orders without a target project. Unlike the in-project import,
 * every row carries its own Project Code — matched to an existing project or
 * used to create one — so a single file can span projects, exactly as an
 * Automatic (Drive) import does.
 */
function ImportWorkOrdersButton() {
  const partnerId = usePartnerId();
  const importsEnabled = useFeatureFlag('imports');
  const [open, setOpen] = useState(false);

  if (!importsEnabled || !partnerId) return null;

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Import Work Orders
      </Button>
      {/* Mounted only while open so the partner configuration it needs is
          fetched on demand, not on every visit to the Projects list. */}
      {open && <ExcelImportDialog open={open} onOpenChange={setOpen} partnerId={partnerId} />}
    </>
  );
}

// ─── Status filter badges ─────────────────────────────────────────

// ENG-830: `redrafting` is a real, filterable `project_status` value now (not
// merely a derived display state), so it gets its own toggle alongside draft
// and released — including for a project with zero eligible work orders.
const statuses: ProjectStatus[] = ['draft', 'released', 'redrafting'];

function StatusFilter({
  active,
  onChange,
}: {
  active: ProjectStatus | null;
  onChange: (status: ProjectStatus | null) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {statuses.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => {
            onChange(active === s ? null : s);
          }}
          className="cursor-pointer"
        >
          <Badge variant={active === s ? 'default' : 'outline'}>{s}</Badge>
        </button>
      ))}
    </div>
  );
}

// ─── Date range cell ────────────────────────────────────────────────

function DateRangeCell({ start, end }: { start: string | null; end: string | null }) {
  if (!start && !end) return <>—</>;
  if (start && end) {
    return (
      <>
        <StandardDate value={start} /> – <StandardDate value={end} />
      </>
    );
  }
  const only = start ?? end;
  return only && formatStandardDate(only) ? <StandardDate value={only} /> : <>—</>;
}

// ─── Main content ─────────────────────────────────────────────────

export function ProjectListContent() {
  const partnerId = usePartnerId();
  const {
    data: projects,
    isLoading,
    isError,
  } = useQuery(projectPlanningListQueryOptions(partnerId));

  const [q, setQ] = useQueryState('q', projectSearchParsers.q);
  const [status, setStatus] = useQueryState('status', projectSearchParsers.status);
  // ENG-819: one dialog instance for the whole table, keyed to the row whose
  // Edit action opened it. Mounting with open is safe — the dialog snapshots
  // its baseline in the open-effect on mount.
  const [editingProject, setEditingProject] = useState<ProjectPlanningListItem | null>(null);

  const filtered = projects ? filterProjects(projects, q, status) : [];

  if (isError) {
    return (
      <div className="text-destructive py-10 text-center text-sm">
        Failed to load projects. Please try again.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-semibold">Projects</h1>
          <ProjectsListViewToggle activeView="grid" />
        </div>
        <div className="flex items-center gap-2">
          <ImportWorkOrdersButton />
          <CreateProjectSheet />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Input
          placeholder="Search projects..."
          value={q ?? ''}
          onChange={(e) => void setQ(e.target.value || null)}
          className="max-w-sm"
        />
        <StatusFilter active={status} onChange={(s) => void setStatus(s)} />
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-muted-foreground py-10 text-center text-sm">
          {q || status
            ? 'No projects match your filters.'
            : 'No projects yet. Create one to get started.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b text-left">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Code</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Factory</th>
                <th className="px-4 py-2 font-medium">Dates</th>
                <th className="px-4 py-2 text-right font-medium">Work Orders</th>
                <th className="px-4 py-2 font-medium">Created by</th>
                <th className="px-4 py-2 font-medium">Milestone</th>
                <th className="px-4 py-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((project) => (
                <tr
                  key={project.project_id}
                  className={`hover:bg-muted/30 border-b transition-colors ${
                    project.is_archived ? 'opacity-60' : ''
                  }`}
                >
                  <td className="px-4 py-2 font-medium">
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: project.project_id }}
                      className="text-foreground hover:underline"
                    >
                      {project.project_name}
                    </Link>
                  </td>
                  <td className="text-muted-foreground px-4 py-2 text-xs">
                    {project.project_code}
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={statusVariant[project.project_status]}>
                      {project.project_status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    {project.factory_names.length === 0 ? (
                      <span className="text-muted-foreground">&mdash;</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {project.factory_names.map((name, idx) => (
                          <Badge key={`${name}-${String(idx)}`} variant="outline">
                            {name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="text-muted-foreground px-4 py-2 text-xs">
                    <DateRangeCell
                      start={project.project_scheduled_start_date}
                      end={project.project_scheduled_complete_date}
                    />
                  </td>
                  <td className="px-4 py-2 text-right">{project.work_order_count}</td>
                  <td className="px-4 py-2">
                    {project.created_by_email ? (
                      <UserInfo
                        name={project.created_by_name}
                        email={project.created_by_email}
                        size="sm"
                      />
                    ) : (
                      <span className="text-muted-foreground">&mdash;</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {(() => {
                      const milestone = resolveProjectMilestone(project);
                      return (
                        <LabeledDate
                          label={milestone.label}
                          date={milestone.date}
                          tone={milestone.tone}
                        />
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <ProjectListRowActions project={project} onEdit={setEditingProject} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingProject && (
        <EditProjectDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditingProject(null);
          }}
          project={editingProject}
        />
      )}
    </div>
  );
}
